import { GearSixIcon } from '@phosphor-icons/react'
import type { MessageCategory, SendTimesSettings, WorkflowSettings } from 'app/types/workflows'
import { workflowCategory } from 'app/lib/workflows/workflowSettings'
import { ChoiceSelect, Field } from './fields'
import { InspectorShell } from './InspectorShell'
import { Section } from './Section'
import { SendTimesEditor } from './SendTimesEditor'

/** Workflow-level texting policy, edited in one place (design language rule 6): the send window
 *  and the texting category. Every SMS step reads these back as one line and links here. */
export function WorkflowSettingsPanel({
  settings,
  onSendTimesChange,
  onCategoryChange,
  canSetCategory,
  onClose,
}: {
  settings: WorkflowSettings
  onSendTimesChange: (patch: Partial<SendTimesSettings>) => void
  onCategoryChange: (category: MessageCategory) => void
  /** Marking a workflow transactional is an admin action; others see the choice but it will not
   *  reach the engine until an admin saves. */
  canSetCategory: boolean
  onClose: () => void
}) {
  const category = workflowCategory(settings)
  return (
    <InspectorShell
      icon={GearSixIcon}
      title="Texting policy"
      subtitle="Applies to every text this workflow sends"
      onClose={onClose}
    >
      <Section title="Send window">
        <SendTimesEditor value={settings.sendTimes} onChange={onSendTimesChange} />
      </Section>
      <Section title="Texting category">
        <Field
          label="Category"
          info="Marketing texts reach only contacts who opted in; everyone else is skipped at send time. Transactional texts (appointments and care) reach everyone with a number. Anything promotional is marketing."
          hint={
            category === `transactional` && !canSetCategory
              ? `Transactional needs a Trackable admin to save this workflow. Until then it sends as marketing.`
              : undefined
          }
        >
          <ChoiceSelect
            value={category}
            choices={[
              { value: `marketing`, label: `Marketing` },
              { value: `transactional`, label: `Transactional` },
            ]}
            onChange={(v) => onCategoryChange(v as MessageCategory)}
          />
        </Field>
      </Section>
    </InspectorShell>
  )
}
